// File: src/components/Reveal/PreRevealCard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { ReactComponent as RevealBoyGraphic } from "../../assets/Reveal Assets/RevealBoyGraphic.svg";
import { ReactComponent as RevealGirlGraphic } from "../../assets/Reveal Assets/RevealGirlGraphic.svg";

/* ───────────────────────── Helpers ───────────────────────── */
function coerceTargetMs(targetMs, targetDate) {
  if (Number.isFinite(targetMs)) return Number(targetMs);
  if (targetDate != null) {
    const t = new Date(targetDate).getTime();
    return Number.isFinite(t) ? t : null;
  }
  return null;
}
function diffToParts(target) {
  if (!Number.isFinite(target)) return { h: "--", m: "--", s: "--" };
  const diff = target - Date.now();
  if (diff <= 0) return { h: "00", m: "00", s: "00" };
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1_000);
  return { h: String(h).padStart(2, "0"), m: String(m).padStart(2, "0"), s: String(s).padStart(2, "0") };
}
function canonicalizeOrientation(raw) {
  const s = String(raw || "").trim().toLowerCase();
  const flat = s.replace(/[^a-z]/g, "");
  if (flat === "gaymen" || flat === "mlm" || flat === "menlovingmen") return "gay_men";
  if (flat === "gaywomen" || flat === "wlw" || flat === "womenlovingwomen" || flat === "lesbian") return "gay_women";
  if (flat === "straight" || flat === "hetero" || flat === "heterosexual") return "straight";
  if (flat === "all" || flat === "any" || flat === "everyone" || flat === "sexuallyfluid") return "all";
  return "all";
}

/* ───────────────────────── Styles ───────────────────────── */
const SVG_SCALE = 0.88; // must match svgWrap width/height percent

const S = {
  container: {
    maxWidth: 600,
    width: "94%",
    margin: "0 auto",
    textAlign: "center",
    padding: "0 0 0",
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    color: "#111827",
  },

  /* Header block (match RevealIntroWizardPage) */
  headerWrap: {
    textAlign: "center",
    maxWidth: 640,
    margin: "0 -",
  },
  headerTop: {
    fontSize: "clamp(1.15rem, 2.6vw, 1.05rem)",
    fontWeight: 900,
    letterSpacing: "0.06em",
    lineHeight: 1.15,
    margin: "-14px 0 0 0",
    color: "#000",
  },
  headerBottom: {
    fontSize: "clamp(1.8rem, 4.2vw, 2.2rem)",
    lineHeight: 1.25,
    marginTop: 8,
    fontWeight: 900,
    letterSpacing: "-0.01em",
    color: "#0B1020",
  },
  subheading: {
    fontSize: 13.5,
    color: "#3A4050",
    lineHeight: 1.55,
    margin: "10px 28px 6px 28px",
  },

  /* Graphics row */
  gfxRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 0,
    margin: "8px 0 14px",
  },
  avatarFrame: {
    position: "relative",
    width: 156,
    height: 156,
    borderRadius: "50%",
    background: "#fff",
    border: "0px solid #E5E7EB",
    display: "grid",
    placeItems: "center",
    overflow: "visible", // allow pulses to grow outward
  },

  // This wrapper matches the SVG footprint (88% of frame) so the ring sits on that boundary.
  ringWrap: {
    position: "absolute",
    width: `${SVG_SCALE * 95}%`,
    height: `${SVG_SCALE * 95}%`,
    borderRadius: "50%",
    zIndex: 0, // behind the SVG
    top: 16,
  },

  // Edge-anchored ring that begins exactly at ringWrap's edge and expands outward
  edgePulse: (delay = 0, duration = 2600, rgb = "37,83,230") => ({
    position: "absolute",
    inset: 0,
    borderRadius: "50%",
    border: `3px solid rgba(${rgb}, 0.55)`,
    transform: "scale(.30)", // start exactly at ringWrap edge (inner circle)
    animation: `revealEdgePulse ${duration}ms ease-out ${delay}ms infinite`,
    pointerEvents: "none",
  }),

  svgWrap: {
    width: `${SVG_SCALE * 100}%`,
    height: `${SVG_SCALE * 100}%`,
    display: "grid",
    placeItems: "center",
    position: "relative",
    zIndex: 1, // above pulses
  },

  /* Countdown (mixer styling) */
  timerContainer: { marginTop: "0.2rem", marginBottom: "1.25rem" },
  timerPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 999,
    padding: ".35rem .9rem",
    fontWeight: 600,
    fontSize: 12,
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: ".04em",
    marginBottom: "0.9rem",
  },
  groupsGrid: {
    display: "grid",
    gridTemplateColumns: "auto auto auto auto auto",
    justifyContent: "center",
    alignItems: "start",
    columnGap: "12px",
  },
  groupCol: { display: "flex", flexDirection: "column", alignItems: "center" },
  digitsRow: {
    display: "flex",
    gap: "4px",
    alignItems: "baseline",
    justifyContent: "center",
  },
  digit: {
    display: "inline-block",
    fontSize: "2.8rem",
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: "-0.02em",
    color: "#0f172a",
    fontVariantNumeric: "tabular-nums",
  },
  digitFaded: { color: "#9ca3af" },
  unitLabel: {
    marginTop: "0.5rem",
    fontSize: 11,
    color: "#6b7280",
    fontWeight: 500,
    letterSpacing: ".02em",
    textTransform: "uppercase",
  },
  colonCol: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    fontSize: "2.2rem",
    fontWeight: 800,
    color: "#9ca3af",
    lineHeight: 1,
    transform: "translateY(6px)",
    marginTop: 0,
  },
};

/* Keyframes (injected once) — ring originates at inner SVG circle edge */
const KF = `
@keyframes revealEdgePulse {
  0%   { transform: scale(1.00); opacity: 0.40; }
  40%  { transform: scale(1.10); opacity: 0.25; }
  100% { transform: scale(1.22); opacity: 0.00; }
}
@media (prefers-reduced-motion: reduce) {
  .reveal-edge-pulse { animation: none !important; }
}
`;

/* ───────────────────────── Component ───────────────────────── */
export default function PreRevealCard({
  targetMs,
  targetDate,
  firstName = "",
  eventName = "Event",
  helperText = "Get ready—your reveal starts soon.",
  orientation = "all",
  onExpire,
  forceEnded = false,
}) {
  const target = useMemo(() => coerceTargetMs(targetMs, targetDate), [targetMs, targetDate]);
  const normOrientation = useMemo(() => canonicalizeOrientation(orientation), [orientation]);

  // Boy/girl visibility
  const { showBoy, showGirl } = useMemo(() => {
    switch (normOrientation) {
      case "gay_men":
        return { showBoy: true, showGirl: false };
      case "gay_women":
        return { showBoy: false, showGirl: true };
      case "straight":
        return { showBoy: true, showGirl: true };
      case "all":
      default:
        return { showBoy: true, showGirl: false };
    }
  }, [normOrientation]);

  const [parts, setParts] = useState(diffToParts(target));
  const [ended, setEnded] = useState(forceEnded || (Number.isFinite(target) && Date.now() >= target));
  const firedRef = useRef(false);

  // Inject keyframes once
  useEffect(() => {
    const id = "reveal-edgepulse-kf";
    if (!document.getElementById(id)) {
      const style = document.createElement("style");
      style.id = id;
      style.innerHTML = KF;
      document.head.appendChild(style);
    }
  }, []);

  // Sync on force
  useEffect(() => {
    if (forceEnded && !ended) {
      setEnded(true);
      setParts({ h: "00", m: "00", s: "00" });
      if (!firedRef.current) {
        firedRef.current = true;
        try { onExpire?.(); } catch {}
      }
    }
  }, [forceEnded, ended, onExpire]);

  // Ticker
  useEffect(() => {
    if (!Number.isFinite(target) || ended || forceEnded) return;
    setParts(diffToParts(target));
    const id = setInterval(() => {
      const next = diffToParts(target);
      setParts(next);
      if (next.h === "00" && next.m === "00" && next.s === "00") {
        clearInterval(id);
        setEnded(true);
        if (!firedRef.current) {
          firedRef.current = true;
          try { onExpire?.(); } catch {}
        }
      }
    }, 1000);
    return () => clearInterval(id);
  }, [target, ended, forceEnded, onExpire]);

  const hoursFaded = parts.h === "00";
  const minutesFaded = parts.m === "00";

  return (
    <div style={S.container}>
      {/* Header (match RevealIntroWizardPage) */}
      <div style={S.headerWrap}>
        <div style={S.headerTop}>
          {`WELCOME ${firstName ? firstName.toUpperCase() : "…" } TO`}
        </div>
        <div style={S.headerBottom}>{eventName}</div>
        <p style={S.subheading}>
          {helperText}
        </p>
      </div>

      {/* Graphics row — ring anchored to the inner SVG circle */}
      <div style={{ ...S.gfxRow, gap: showBoy && showGirl ? 24 : 0 }} aria-hidden>
        {showBoy && (
          <div style={S.avatarFrame}>
            <div style={S.ringWrap}>
              <div className="reveal-edge-pulse" style={S.edgePulse(0, 2300)} />
              <div className="reveal-edge-pulse" style={S.edgePulse(700, 3100)} />
            </div>
            <div style={S.svgWrap}>
              <RevealBoyGraphic style={{ width: "100%", height: "100%" }} />
            </div>
          </div>
        )}

        {showGirl && (
          <div style={S.avatarFrame}>
            <div style={S.ringWrap}>
              <div className="reveal-edge-pulse" style={S.edgePulse(0, 2400)} />
              <div className="reveal-edge-pulse" style={S.edgePulse(800, 3200)} />
            </div>
            <div style={S.svgWrap}>
              <RevealGirlGraphic style={{ width: "100%", height: "100%" }} />
            </div>
          </div>
        )}
      </div>

      {/* Countdown (same UI as mixer) */}
      <div style={S.timerContainer}>
        <div style={S.timerPill}>{ended ? "Revealing…" : "Reveal in"}</div>

        <div style={S.groupsGrid} aria-live="polite">
          {/* Hours */}
          <div style={S.groupCol}>
            <div style={S.digitsRow}>
              <span style={{ ...S.digit, ...(hoursFaded ? S.digitFaded : {}) }}>{parts.h[0]}</span>
              <span style={{ ...S.digit, ...(hoursFaded ? S.digitFaded : {}) }}>{parts.h[1]}</span>
            </div>
            <div style={S.unitLabel}>Hours</div>
          </div>

          <div style={S.colonCol}>:</div>

          {/* Minutes */}
          <div style={S.groupCol}>
            <div style={S.digitsRow}>
              <span style={{ ...S.digit, ...(minutesFaded ? S.digitFaded : {}) }}>{parts.m[0]}</span>
              <span style={{ ...S.digit, ...(minutesFaded ? S.digitFaded : {}) }}>{parts.m[1]}</span>
            </div>
            <div style={S.unitLabel}>Minutes</div>
          </div>

          <div style={S.colonCol}>:</div>

          {/* Seconds */}
          <div style={S.groupCol}>
            <div style={S.digitsRow}>
              <span style={S.digit}>{parts.s[0]}</span>
              <span style={S.digit}>{parts.s[1]}</span>
            </div>
            <div style={S.unitLabel}>Seconds</div>
          </div>
        </div>
      </div>
    </div>
  );
}
